import React, { createContext, useContext } from "react";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import type { EntityState, ExportState, FilterState } from "./types";

/**
 * UniversalEntityPageUIContext
 *
 * Contains all UI-related state:
 * - filterState: Search, filters, advanced filters, active view
 * - entityState: Editing item, deleting item, modal visibility
 * - exportState: Export progress, export modal visibility
 *
 * Separated from other contexts to prevent unnecessary re-renders
 * when data or handlers change.
 */
export interface UniversalEntityPageUIContextValue<T extends BaseEntity> {
  filterState: FilterState<T>;
  entityState: EntityState<T>;
  exportState: ExportState;
}

const UniversalEntityPageUIContext = createContext<
  UniversalEntityPageUIContextValue<any> | undefined
>(undefined);

export function UniversalEntityPageUIProvider<T extends BaseEntity>({
  children,
  value,
}: {
  children: React.ReactNode;
  value: UniversalEntityPageUIContextValue<T>;
}) {
  return (
    <UniversalEntityPageUIContext.Provider value={value}>
      {children}
    </UniversalEntityPageUIContext.Provider>
  );
}

export function useUniversalEntityPageUIContext<T extends BaseEntity>() {
  const context = useContext(UniversalEntityPageUIContext);
  if (!context) {
    throw new Error(
      "useUniversalEntityPageUIContext must be used within UniversalEntityPageUIProvider"
    );
  }
  return context as UniversalEntityPageUIContextValue<T>;
}
